import {
  Badge,
  cn,
  Icon,
  type IconName,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui";

import type { PersonaOpportunitySet } from "../../../types/persona-opportunities.types.js";
import type { OpportunityHubSelectionLevel } from "./hub-chrome-selection.js";
import { OpportunityHubNameList } from "./OpportunityHubNameList.js";
import { OpportunityHubSetParams } from "./OpportunityHubSetBadge.js";

const TOOLTIP_DELAY_MS = 300;

/** Slate chip sized for hub metadata, tinting on hover to show it has more to say. */
export const HUB_SLATE_CHIP_CLASS =
  "h-6 text-[11px] transition-colors duration-150 ease-out hover:bg-[color-mix(in_oklab,var(--slate-chip-bg),var(--slate-chip-text)_10%)] motion-reduce:transition-none";

/** Slate metadata chip, like the set and angle badges, with a clear affordance. */
const SELECTION_PILL_CLASS = cn(
  HUB_SLATE_CHIP_CLASS,
  "group max-w-[16rem] cursor-pointer outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40",
);

export const HUB_SELECTION_LEVEL_ICON: Record<OpportunityHubSelectionLevel, IconName> = {
  sets: "folder",
  opportunities: "target",
  scripts: "file-text",
};

/**
 * A tab's own selection, with a clear button. Hovering one selected set lists its
 * parameters; several selected sets, opportunities, or scripts list their names.
 */
export function OpportunityHubSelectionPill({
  level,
  label,
  sets,
  itemNames = [],
  itemCount,
  clearAriaLabel,
  onClear,
  className,
}: {
  level: OpportunityHubSelectionLevel;
  label: string;
  /** The selected sets, when the selection is of sets. */
  sets: readonly PersonaOpportunitySet[];
  /** Names of the selected opportunities or scripts that have loaded. */
  itemNames?: readonly string[];
  /** Selected opportunities or scripts, named or not. */
  itemCount?: number;
  clearAriaLabel: string;
  onClear: () => void;
  className?: string;
}) {
  const pill = (
    <Badge asChild variant="slate" className={cn(SELECTION_PILL_CLASS, className)}>
      <button type="button" onClick={onClear} aria-label={clearAriaLabel}>
        <Icon name={HUB_SELECTION_LEVEL_ICON[level]} size="xs" color="inherit" aria-hidden />
        <span className="min-w-0 truncate">{label}</span>
        <Icon
          name="x"
          size="xs"
          color="inherit"
          aria-hidden
          className="opacity-60 transition-opacity duration-150 ease-out group-hover:opacity-100 motion-reduce:transition-none"
        />
      </button>
    </Badge>
  );
  const [onlySet] = sets;
  if (!onlySet && itemNames.length === 0) return pill;

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent align="start" className="px-3 py-2">
          {onlySet && sets.length === 1 ? (
            <OpportunityHubSetParams set={onlySet} />
          ) : onlySet ? (
            <OpportunityHubNameList names={sets.map((set) => set.name)} />
          ) : (
            <OpportunityHubNameList names={itemNames} total={itemCount} />
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
